<script setup>
/**
 * KaptionQuickFilters
 *
 * The views row under the search: ONE horizontally scrolling row with every
 * view (Todas · No leídas · Mías · Sin atender · Sin asignar · Menciones),
 * then ONE button pinned at the right end: the current status (its icon,
 * expanding to the label on hover/focus) whose menu holds Estado, Ordenar por
 * and "Más filtros…" (the advanced panel: people, inboxes, teams, labels,
 * folders, assistants, new filter). It is tinted while a filter is applied. Views live only here, never split with
 * the panel; mobile uses the same layout (mobile-filters parity).
 *
 * NOTICE: KAPTION-INTEGRATION
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { SIDEBAR_ENTRY_PERMISSION_KEYS } from 'dashboard/components-next/sidebar/kaptionSidebarFilterGates';

const props = defineProps({
  // ChatList's assigneeTabItems: [{ key, name, count, tooltip }], already
  // filtered by permissions and account settings.
  assigneeTabs: { type: Array, default: () => [] },
  currentAssigneeFilter: { type: String, default: 'all' },
  currentStatusFilter: { type: String, default: 'open' },
  currentSort: {
    type: String,
    default: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  },
  // On All conversations / Mentions / Unattended the chips navigate back to
  // the home route; inside a label, inbox, team... they filter in place.
  navigates: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  // 'mention' / 'unattended' when the list is one of those views.
  conversationType: { type: String, default: '' },
  // Something from the Filters panel (label, inbox, folder, ...) is applied.
  filtersActive: { type: Boolean, default: false },
  // Name of that applied filter, shown in the menu with "Quitar".
  activeFilterName: { type: String, default: '' },
  // ai-takeover.LIST.2 — "Quién atiende": '', 'assistant', 'human' or 'waiting'.
  showHandlerFilter: { type: Boolean, default: false },
  currentHandlerFilter: { type: String, default: '' },
});

const emit = defineEmits([
  'filterChange',
  'navigateToAllConversations',
  'navigateToUnattended',
  'navigateToMentions',
  'statusFilterChange',
  'sortChange',
  'openFilters',
  'clearFilters',
  'handlerFilterChange',
]);

const { t } = useI18n();
const { canUseFeature } = useFeaturePermissions();

// ChatList's tab keys → the topbar vocabulary ChatList's handlers expect.
const TO_FILTER = { me: 'mine', unassigned: 'unassigned', unread: 'unread' };
// Same icons the topbar's filter pill used. Unread stays text-only on purpose
// (unread-filter.FILTER_PILL.2: it leads with the label and the count).
const CHIP_ICONS = {
  me: 'i-lucide-user-check',
  unattended: 'i-lucide-message-circle-warning',
  unassigned: 'i-lucide-circle-dashed',
  mentions: 'i-lucide-at-sign',
};

const onRouteView = computed(() =>
  ['mention', 'unattended'].includes(props.conversationType)
);

const chips = computed(() => {
  const tab = key => props.assigneeTabs.find(item => item.key === key);
  const assignee = key =>
    tab(key) && {
      ...tab(key),
      kind: 'assignee',
      active: !onRouteView.value && props.currentAssigneeFilter === key,
    };
  const routeView = (key, type, name, permission) =>
    canUseFeature(SIDEBAR_ENTRY_PERMISSION_KEYS[permission]) && {
      key,
      name,
      kind: 'route',
      active: props.conversationType === type,
    };
  return [
    {
      key: 'all',
      name: t('KAPTION_PLATFORM.VIEW_SWITCHER.ALL_CHIP'),
      kind: 'all',
      active: !onRouteView.value && props.currentAssigneeFilter === 'all',
    },
    assignee('unread'),
    assignee('me'),
    routeView(
      'unattended',
      'unattended',
      t('KAPTION_PLATFORM.FILTERS.UNATTENDED'),
      'Unattended'
    ),
    assignee('unassigned'),
    routeView(
      'mentions',
      'mention',
      t('KAPTION_PLATFORM.FILTERS.MENTIONS'),
      'Mentions'
    ),
  ]
    .filter(Boolean)
    .map(chip => ({ ...chip, icon: CHIP_ICONS[chip.key] || '' }));
});

// Navigation chips (Todas, Sin atender, Menciones) always work — they are how
// you leave a folder or an applied filter. Assignee chips narrow the current
// list, so they're off while a folder/advanced filter owns it.
const isChipDisabled = chip => props.disabled && chip.kind === 'assignee';

function goAll() {
  if (props.navigates || onRouteView.value)
    emit('navigateToAllConversations', 'all');
  else emit('filterChange', 'all');
}

function toggleChip(chip) {
  if (isChipDisabled(chip)) return;
  if (chip.kind === 'all') {
    goAll();
  } else if (chip.kind === 'route') {
    if (chip.active) goAll();
    else if (chip.key === 'mentions') emit('navigateToMentions');
    else emit('navigateToUnattended');
  } else {
    const target = chip.active ? 'all' : TO_FILTER[chip.key];
    if (props.navigates || onRouteView.value)
      emit('navigateToAllConversations', target);
    else emit('filterChange', target);
  }
}

const STATUSES = ['open', 'snoozed', 'resolved', 'all'];
const STATUS_ICONS = {
  open: 'i-lucide-circle',
  snoozed: 'i-lucide-alarm-clock',
  resolved: 'i-lucide-check-circle',
  all: 'i-lucide-layers',
};
const isStatusMenuOpen = ref(false);
// The two pinned buttons share the chips' shape and height (alternative B).
const PINNED_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-0.5 h-7 px-2 rounded-full outline outline-1 shrink-0 transition-colors';
const statusLabel = status =>
  t(`KAPTION_PLATFORM.STATUS.${status.toUpperCase()}`);

const statusButtonLabel = computed(
  () =>
    `${t('KAPTION_PLATFORM.VIEW_SWITCHER.STATUS')}: ${statusLabel(props.currentStatusFilter)}`
);

function selectStatus(status) {
  isStatusMenuOpen.value = false;
  if (status !== props.currentStatusFilter) emit('statusFilterChange', status);
}

const { SORT_BY_TYPE } = wootConstants;
const SORT_OPTIONS = [
  SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  SORT_BY_TYPE.LAST_ACTIVITY_AT_ASC,
  SORT_BY_TYPE.CREATED_AT_DESC,
  SORT_BY_TYPE.CREATED_AT_ASC,
  SORT_BY_TYPE.PRIORITY_DESC,
  SORT_BY_TYPE.PRIORITY_ASC,
  SORT_BY_TYPE.WAITING_SINCE_ASC,
  SORT_BY_TYPE.WAITING_SINCE_DESC,
];
const sortLabel = value => t(`CHAT_LIST.SORT_ORDER_ITEMS.${value}.TEXT`);

function openMoreFilters() {
  isStatusMenuOpen.value = false;
  emit('openFilters');
}

function clearActiveFilter() {
  isStatusMenuOpen.value = false;
  emit('clearFilters');
}

// ai-takeover.LIST.2 — who is handling the conversation. The active choice also shows as a
// removable chip at the head of the row, so the narrowed list never looks like the whole list.
const HANDLERS = [
  { key: '', icon: 'i-lucide-users' },
  { key: 'assistant', icon: 'i-lucide-sparkles' },
  { key: 'human', icon: 'i-lucide-user-round' },
  { key: 'waiting', icon: 'i-lucide-circle-dashed' },
];
const handlerLabel = key =>
  t(`KAPTION_PLATFORM.AI_TAKEOVER.HANDLER.${(key || 'all').toUpperCase()}`);
const activeHandler = computed(() =>
  HANDLERS.find(
    handler => handler.key && handler.key === props.currentHandlerFilter
  )
);

function selectHandler(key) {
  isStatusMenuOpen.value = false;
  if (key !== props.currentHandlerFilter) emit('handlerFilterChange', key);
}

function selectSort(value) {
  isStatusMenuOpen.value = false;
  if (value !== props.currentSort) emit('sortChange', value);
}
</script>

<template>
  <div
    class="flex items-center gap-1 pb-2 ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
    data-testid="kaption-quick-filters"
  >
    <!-- The views scroll; a fade at the right edge says there's more. -->
    <div class="relative flex-1 min-w-0 ltr:mr-1 rtl:ml-1">
      <div
        class="flex items-center flex-1 min-w-0 gap-1 py-px overflow-x-auto no-scrollbar"
      >
        <button
          v-if="showHandlerFilter && activeHandler"
          type="button"
          data-testid="kaption-handler-chip"
          class="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded-full outline outline-1 whitespace-nowrap shrink-0 bg-n-iris-3 text-n-iris-11 outline-n-iris-7"
          :aria-label="`${t('KAPTION_PLATFORM.AI_TAKEOVER.HANDLER.TITLE')}: ${handlerLabel(activeHandler.key)}`"
          @click="selectHandler('')"
        >
          <span class="size-3.5 shrink-0" :class="activeHandler.icon" />
          {{ handlerLabel(activeHandler.key) }}
          <span class="i-lucide-x size-3 opacity-70" />
        </button>
        <button
          v-for="chip in chips"
          :key="chip.key"
          v-tooltip.bottom="chip.tooltip"
          type="button"
          :aria-pressed="chip.active"
          :disabled="isChipDisabled(chip)"
          :data-testid="`kaption-quick-filter-${chip.key}`"
          class="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded-full outline outline-1 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :class="
            chip.active
              ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-7'
              : 'bg-n-solid-1 text-n-slate-12 outline-n-weak hover:bg-n-alpha-2'
          "
          @click="toggleChip(chip)"
        >
          <span v-if="chip.icon" class="size-3.5 shrink-0" :class="chip.icon" />
          {{ chip.name }}
          <span
            v-if="chip.key === 'unread' && chip.count > 0"
            class="inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] font-bold text-white rounded-full bg-n-teal-10"
          >
            {{ chip.count }}
          </span>
        </button>
      </div>
      <div
        class="absolute inset-y-0 w-8 pointer-events-none ltr:right-0 rtl:left-0 ltr:bg-gradient-to-l rtl:bg-gradient-to-r from-n-solid-1 to-transparent"
      />
    </div>

    <div v-on-clickaway="() => (isStatusMenuOpen = false)" class="relative">
      <button
        v-tooltip.bottom="statusButtonLabel"
        type="button"
        data-testid="kaption-status-menu"
        :aria-label="statusButtonLabel"
        :aria-expanded="isStatusMenuOpen"
        :aria-pressed="filtersActive || !!activeHandler"
        class="group"
        :class="[
          PINNED_BUTTON_CLASS,
          filtersActive || activeHandler
            ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-7'
            : 'bg-n-solid-1 text-n-slate-12 outline-n-weak hover:bg-n-alpha-2',
        ]"
        @click="isStatusMenuOpen = !isStatusMenuOpen"
      >
        <!-- The only filter control: the current status's own icon, expanding
             to its label on hover/focus so it says what it is. -->
        <span class="size-3.5" :class="STATUS_ICONS[currentStatusFilter]" />
        <span
          data-testid="kaption-status-label"
          class="max-w-0 overflow-hidden text-xs font-medium whitespace-nowrap transition-[max-width] duration-200 group-hover:max-w-[8rem] group-focus-visible:max-w-[8rem]"
          :class="{ 'max-w-[8rem]': isStatusMenuOpen }"
        >
          {{ statusLabel(currentStatusFilter) }}
        </span>
        <span class="i-lucide-chevron-down size-3 opacity-70" />
      </button>
      <div
        v-if="isStatusMenuOpen"
        class="absolute z-50 py-1 mt-1 border shadow-lg top-full ltr:right-0 rtl:left-0 w-64 max-h-[70vh] overflow-y-auto rounded-xl bg-n-solid-1 border-n-weak"
      >
        <p
          class="px-3 pt-1.5 pb-1 text-[11px] font-semibold tracking-wider uppercase text-n-slate-11"
        >
          {{ t('KAPTION_PLATFORM.VIEW_SWITCHER.STATUS') }}
        </p>
        <button
          v-for="status in STATUSES"
          :key="status"
          type="button"
          class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
          @click="selectStatus(status)"
        >
          <span class="size-4 text-n-slate-11" :class="STATUS_ICONS[status]" />
          <span class="flex-1">{{ statusLabel(status) }}</span>
          <span
            v-if="status === currentStatusFilter"
            class="i-lucide-check size-4 text-n-teal-10"
          />
        </button>
        <!-- ai-takeover.LIST.2 -->
        <template v-if="showHandlerFilter">
          <div class="h-px mx-3 my-1 bg-n-weak" />
          <p
            class="px-3 pt-1.5 pb-1 text-[11px] font-semibold tracking-wider uppercase text-n-slate-11"
          >
            {{ t('KAPTION_PLATFORM.AI_TAKEOVER.HANDLER.TITLE') }}
          </p>
          <button
            v-for="handler in HANDLERS"
            :key="handler.key || 'all'"
            type="button"
            :data-testid="`kaption-handler-${handler.key || 'all'}`"
            class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
            @click="selectHandler(handler.key)"
          >
            <span class="size-4 text-n-slate-11" :class="handler.icon" />
            <span class="flex-1">{{ handlerLabel(handler.key) }}</span>
            <span
              v-if="handler.key === (currentHandlerFilter || '')"
              class="i-lucide-check size-4 text-n-teal-10"
            />
          </button>
        </template>
        <div class="h-px mx-3 my-1 bg-n-weak" />
        <p
          class="px-3 pt-1.5 pb-1 text-[11px] font-semibold tracking-wider uppercase text-n-slate-11"
        >
          {{ t('CHAT_LIST.CHAT_SORT.ORDER_BY') }}
        </p>
        <button
          v-for="value in SORT_OPTIONS"
          :key="value"
          type="button"
          :data-testid="`kaption-sort-${value}`"
          class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
          @click="selectSort(value)"
        >
          <span class="flex-1">{{ sortLabel(value) }}</span>
          <span
            v-if="value === currentSort"
            class="i-lucide-check size-4 text-n-teal-10"
          />
        </button>
        <div class="h-px mx-3 my-1 bg-n-weak" />
        <div
          v-if="filtersActive && activeFilterName"
          class="flex items-center gap-2 px-3 py-1.5 text-sm"
          data-testid="kaption-active-filter"
        >
          <span class="i-lucide-list-filter size-4 text-n-teal-10" />
          <span class="flex-1 truncate text-n-slate-12">{{
            activeFilterName
          }}</span>
          <button
            type="button"
            data-testid="kaption-clear-active-filter"
            class="text-xs font-semibold text-n-ruby-11 hover:underline"
            @click="clearActiveFilter"
          >
            {{ t('KAPTION_PLATFORM.VIEW_SWITCHER.CLEAR_FILTER') }}
          </button>
        </div>
        <button
          type="button"
          data-testid="kaption-more-filters"
          class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left font-medium text-n-teal-11 hover:bg-n-alpha-2"
          @click="openMoreFilters"
        >
          <span class="i-lucide-list-filter size-4" />
          <span class="flex-1">{{
            t('KAPTION_PLATFORM.VIEW_SWITCHER.MORE_FILTERS')
          }}</span>
          <span class="i-lucide-chevron-right size-4 opacity-60" />
        </button>
      </div>
    </div>
  </div>
</template>
